/**
 * Generated by orval v8.22.0 🍺
 * Do not edit manually.
 * PepsiCo Geospatial UAV API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";

import type {
  BodyUploadPlotsCsv,
  BodyUploadSitesCsv,
  BodyUploadTrialsCsv,
  CreateImageUploadsRequest,
  CreateImageUploadsResponse,
  HTTPValidationError,
  TaskResponse,
} from "../model";

export type createImageUploadsResponse200 = {
  data: CreateImageUploadsResponse;
  status: 200;
};

export type createImageUploadsResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type createImageUploadsResponseSuccess =
  createImageUploadsResponse200 & {
    headers: Headers;
  };
export type createImageUploadsResponseError = createImageUploadsResponse422 & {
  headers: Headers;
};

export type createImageUploadsResponse =
  createImageUploadsResponseSuccess | createImageUploadsResponseError;

export const getCreateImageUploadsUrl = () => {
  return `/api/uploads/bulk`;
};

/**
 * Creates one pending upload task per new JPEG, each with a tracking progress entry that includes the ortho id and a long-lived presigned S3 PUT URL under uploads/{ortho_uuid}/raw/. Task descriptions are the directory path joined with the filename. All files in one request must share the same site_id (422 if they don't) and must match the ortho's site, location, and flight date. The ortho must be in uploading status. Non-JPEG filenames and duplicate content hashes (already completed/in-progress, or repeated in the request) are skipped and returned in ``rejected``; other files still succeed. A superseded raw_image_upload, including one released because its ortho was rejected, does not block a new upload of that hash.
 * @summary Initiate bulk JPEG image uploads
 */
export const createImageUploads = async (
  createImageUploadsRequest: CreateImageUploadsRequest,
  options?: RequestInit,
): Promise<createImageUploadsResponse> => {
  const res = await fetch(getCreateImageUploadsUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createImageUploadsRequest),
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: createImageUploadsResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createImageUploadsResponse;
};

export const getCreateImageUploadsMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createImageUploads>>,
    TError,
    { data: CreateImageUploadsRequest },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createImageUploads>>,
  TError,
  { data: CreateImageUploadsRequest },
  TContext
> => {
  const mutationKey = ["createImageUploads"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createImageUploads>>,
    { data: CreateImageUploadsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createImageUploads(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateImageUploadsMutationResult = NonNullable<
  Awaited<ReturnType<typeof createImageUploads>>
>;
export type CreateImageUploadsMutationBody = CreateImageUploadsRequest;
export type CreateImageUploadsMutationError = HTTPValidationError;

/**
 * @summary Initiate bulk JPEG image uploads
 */
export const useCreateImageUploads = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createImageUploads>>,
      TError,
      { data: CreateImageUploadsRequest },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createImageUploads>>,
  TError,
  { data: CreateImageUploadsRequest },
  TContext
> => {
  return useMutation(
    getCreateImageUploadsMutationOptions(options),
    queryClient,
  );
};
export type uploadSitesCsvResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type uploadSitesCsvResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type uploadSitesCsvResponseSuccess = uploadSitesCsvResponse200 & {
  headers: Headers;
};
export type uploadSitesCsvResponseError = uploadSitesCsvResponse422 & {
  headers: Headers;
};

export type uploadSitesCsvResponse =
  uploadSitesCsvResponseSuccess | uploadSitesCsvResponseError;

export const getUploadSitesCsvUrl = () => {
  return `/api/uploads/csv/sites`;
};

/**
 * Stores the CSV at csv/{hash[:2]}/{hash}.csv (skipped when that object already exists), creates a pending upload_sites_csv task, and enqueues processing. The worker downloads the file and upserts sites by external_site_uuid. grower_id is optional; omit the column to leave an existing grower association unchanged. The same file may be uploaded again. Unmapped columns are ignored. Per-row errors and created/updated/unchanged counts are stored on the task results when processing finishes.
 * @summary Upload a sites CSV
 */
export const uploadSitesCsv = async (
  bodyUploadSitesCsv: BodyUploadSitesCsv,
  options?: RequestInit,
): Promise<uploadSitesCsvResponse> => {
  const formData = new FormData();
  formData.append(`file`, bodyUploadSitesCsv.file);

  const res = await fetch(getUploadSitesCsvUrl(), {
    ...options,
    method: "POST",
    body: formData,
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: uploadSitesCsvResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as uploadSitesCsvResponse;
};

export const getUploadSitesCsvMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadSitesCsv>>,
    TError,
    { data: BodyUploadSitesCsv },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadSitesCsv>>,
  TError,
  { data: BodyUploadSitesCsv },
  TContext
> => {
  const mutationKey = ["uploadSitesCsv"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadSitesCsv>>,
    { data: BodyUploadSitesCsv }
  > = (props) => {
    const { data } = props ?? {};

    return uploadSitesCsv(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadSitesCsvMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadSitesCsv>>
>;
export type UploadSitesCsvMutationBody = BodyUploadSitesCsv;
export type UploadSitesCsvMutationError = HTTPValidationError;

/**
 * @summary Upload a sites CSV
 */
export const useUploadSitesCsv = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadSitesCsv>>,
      TError,
      { data: BodyUploadSitesCsv },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadSitesCsv>>,
  TError,
  { data: BodyUploadSitesCsv },
  TContext
> => {
  return useMutation(getUploadSitesCsvMutationOptions(options), queryClient);
};
export type uploadTrialsCsvResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type uploadTrialsCsvResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type uploadTrialsCsvResponseSuccess = uploadTrialsCsvResponse200 & {
  headers: Headers;
};
export type uploadTrialsCsvResponseError = uploadTrialsCsvResponse422 & {
  headers: Headers;
};

export type uploadTrialsCsvResponse =
  uploadTrialsCsvResponseSuccess | uploadTrialsCsvResponseError;

export const getUploadTrialsCsvUrl = () => {
  return `/api/uploads/csv/trials`;
};

/**
 * Stores the CSV at csv/{hash[:2]}/{hash}.csv (skipped when that object already exists), creates a pending upload_trials_csv task, and enqueues processing. The worker downloads the file and upserts trials by external_trial_id. The same file may be uploaded again. Unmapped columns are ignored. Per-row errors and created/updated/unchanged counts are stored on the task results when processing finishes.
 * @summary Upload a trials CSV
 */
export const uploadTrialsCsv = async (
  bodyUploadTrialsCsv: BodyUploadTrialsCsv,
  options?: RequestInit,
): Promise<uploadTrialsCsvResponse> => {
  const formData = new FormData();
  formData.append(`file`, bodyUploadTrialsCsv.file);

  const res = await fetch(getUploadTrialsCsvUrl(), {
    ...options,
    method: "POST",
    body: formData,
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: uploadTrialsCsvResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as uploadTrialsCsvResponse;
};

export const getUploadTrialsCsvMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadTrialsCsv>>,
    TError,
    { data: BodyUploadTrialsCsv },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadTrialsCsv>>,
  TError,
  { data: BodyUploadTrialsCsv },
  TContext
> => {
  const mutationKey = ["uploadTrialsCsv"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadTrialsCsv>>,
    { data: BodyUploadTrialsCsv }
  > = (props) => {
    const { data } = props ?? {};

    return uploadTrialsCsv(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadTrialsCsvMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadTrialsCsv>>
>;
export type UploadTrialsCsvMutationBody = BodyUploadTrialsCsv;
export type UploadTrialsCsvMutationError = HTTPValidationError;

/**
 * @summary Upload a trials CSV
 */
export const useUploadTrialsCsv = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadTrialsCsv>>,
      TError,
      { data: BodyUploadTrialsCsv },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadTrialsCsv>>,
  TError,
  { data: BodyUploadTrialsCsv },
  TContext
> => {
  return useMutation(getUploadTrialsCsvMutationOptions(options), queryClient);
};
export type uploadPlotsCsvResponse200 = {
  data: TaskResponse;
  status: 200;
};

export type uploadPlotsCsvResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type uploadPlotsCsvResponseSuccess = uploadPlotsCsvResponse200 & {
  headers: Headers;
};
export type uploadPlotsCsvResponseError = uploadPlotsCsvResponse422 & {
  headers: Headers;
};

export type uploadPlotsCsvResponse =
  uploadPlotsCsvResponseSuccess | uploadPlotsCsvResponseError;

export const getUploadPlotsCsvUrl = () => {
  return `/api/uploads/csv/plots`;
};

/**
 * Stores the CSV at csv/{hash[:2]}/{hash}.csv (skipped when that object already exists), creates a pending upload_plots_csv task, and enqueues processing. The worker downloads the file and upserts plots by external_plot_uuid. CSV external_trial_id matches trials_geo.external_trial_id; the stored plots_geo.trial_id is trials_geo.id. The same file may be uploaded again. Unmapped columns are ignored. Per-row errors and created/updated/unchanged counts are stored on the task results when processing finishes.
 * @summary Upload a plots CSV
 */
export const uploadPlotsCsv = async (
  bodyUploadPlotsCsv: BodyUploadPlotsCsv,
  options?: RequestInit,
): Promise<uploadPlotsCsvResponse> => {
  const formData = new FormData();
  formData.append(`file`, bodyUploadPlotsCsv.file);

  const res = await fetch(getUploadPlotsCsvUrl(), {
    ...options,
    method: "POST",
    body: formData,
  });

  const body = [204, 205, 304].includes(res.status) ? null : await res.text();

  const data: uploadPlotsCsvResponse["data"] = body ? JSON.parse(body) : {};
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as uploadPlotsCsvResponse;
};

export const getUploadPlotsCsvMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadPlotsCsv>>,
    TError,
    { data: BodyUploadPlotsCsv },
    TContext
  >;
  fetch?: RequestInit;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadPlotsCsv>>,
  TError,
  { data: BodyUploadPlotsCsv },
  TContext
> => {
  const mutationKey = ["uploadPlotsCsv"];
  const { mutation: mutationOptions, fetch: fetchOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, fetch: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadPlotsCsv>>,
    { data: BodyUploadPlotsCsv }
  > = (props) => {
    const { data } = props ?? {};

    return uploadPlotsCsv(data, fetchOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadPlotsCsvMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadPlotsCsv>>
>;
export type UploadPlotsCsvMutationBody = BodyUploadPlotsCsv;
export type UploadPlotsCsvMutationError = HTTPValidationError;

/**
 * @summary Upload a plots CSV
 */
export const useUploadPlotsCsv = <
  TError = HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadPlotsCsv>>,
      TError,
      { data: BodyUploadPlotsCsv },
      TContext
    >;
    fetch?: RequestInit;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadPlotsCsv>>,
  TError,
  { data: BodyUploadPlotsCsv },
  TContext
> => {
  return useMutation(getUploadPlotsCsvMutationOptions(options), queryClient);
};
